import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBell,
  faBellSlash,
  faCheck,
  faClock,
  faCloudArrowUp,
  faInbox,
  faPlus,
  faSliders,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

import {
  assinarFila,
  enfileirar,
  flush,
  garantirSincronizacaoAutomatica,
} from "../services/captureQueue";
import {
  ativarNotificacoes,
  permissaoAtual,
  precisaInstalarParaNotificar,
  pushSuportado,
} from "../services/pushClient";
import PersonalizarLembreteModal from "../components/PersonalizarLembreteModal";
import { formatarLembrarEm, parseReminderInput } from "../utils/reminderParser";
import {
  REMINDER_ORIGIN,
  REMINDER_RECURRENCE,
  ehRecorrente,
  getRecurrenceLabel,
} from "../utils/reminderStatus";
import "./CapturarPage.css";

const CHIPS = [
  { id: "30min", rotulo: "Em 30min" },
  { id: "1h", rotulo: "Em 1h" },
  { id: "hoje18", rotulo: "Hoje 18h" },
  { id: "amanha9", rotulo: "Amanha 9h" },
];

function calcularHoraRapida(chip) {
  if (!chip) return null;

  const data = new Date();

  if (chip === "30min") {
    data.setMinutes(data.getMinutes() + 30, 0, 0);
    return data;
  }

  if (chip === "1h") {
    data.setHours(data.getHours() + 1, data.getMinutes(), 0, 0);
    return data;
  }

  if (chip === "hoje18") {
    data.setHours(18, 0, 0, 0);
    if (data.getTime() <= Date.now()) data.setDate(data.getDate() + 1);
    return data;
  }

  if (chip === "amanha9") {
    data.setDate(data.getDate() + 1);
    data.setHours(9, 0, 0, 0);
    return data;
  }

  return null;
}

/** Monta o texto inicial quando a pagina foi aberta pelo "compartilhar com". */
function textoCompartilhado(searchParams) {
  const partes = [
    searchParams.get("title"),
    searchParams.get("text"),
    searchParams.get("url"),
  ].filter(Boolean);

  // O Android costuma repetir a URL dentro de text; evita duplicar.
  const unicas = partes.filter((parte, indice) => partes.indexOf(parte) === indice);
  return unicas.join(" ").trim();
}

const CapturarPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [valor, setValor] = useState("");
  const [chipAtivo, setChipAtivo] = useState(null);
  const [personalizado, setPersonalizado] = useState(null);
  const [mostrarPersonalizar, setMostrarPersonalizar] = useState(false);
  const [capturados, setCapturados] = useState([]);
  const [uuidsPendentes, setUuidsPendentes] = useState([]);
  const [erro, setErro] = useState("");
  const [convitePush, setConvitePush] = useState(false);
  const [ativandoPush, setAtivandoPush] = useState(false);

  const inputRef = useRef(null);

  const analise = useMemo(() => parseReminderInput(valor), [valor]);

  // Precedencia: o que foi digitado > o personalizado > o chip rapido.
  const lembrarEm =
    analise.lembrarEm || personalizado?.lembrarEm || calcularHoraRapida(chipAtivo);
  const recorrencia = analise.lembrarEm
    ? REMINDER_RECURRENCE.NENHUMA
    : personalizado?.recorrencia || REMINDER_RECURRENCE.NENHUMA;
  const textoFinal = (analise.texto || "").trim();

  useEffect(() => {
    garantirSincronizacaoAutomatica();
    const cancelar = assinarFila((fila) => {
      setUuidsPendentes(fila.map((item) => item.client_uuid));
    });
    return cancelar;
  }, []);

  // Abertura via "compartilhar com o LevelUp" (share_target do manifest).
  useEffect(() => {
    const compartilhado = textoCompartilhado(searchParams);

    if (compartilhado) {
      setValor(compartilhado);
      // Limpa a query para um F5 nao repreencher o campo.
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // O autoFocus do input resolve o caso normal. Este reforco cobre o resto:
  // volta do modal, retorno para a aba e o mount atrasado depois do loading do
  // useAuth. Em celular o teclado ainda exige um toque - o navegador nao deixa
  // abri-lo sem gesto do usuario, e nao ha contorno para isso.
  useEffect(() => {
    const focar = () => {
      const campo = inputRef.current;
      if (!campo || campo === document.activeElement) return;

      campo.focus();
      // Cursor no fim, importante quando o texto veio do "compartilhar com".
      const fim = campo.value.length;
      campo.setSelectionRange?.(fim, fim);
    };

    focar();
    const quadro = window.requestAnimationFrame(focar);

    return () => window.cancelAnimationFrame(quadro);
  }, []);

  const capturar = useCallback(() => {
    const texto = textoFinal || valor.trim();

    if (!texto) return;

    const item = enfileirar({
      texto,
      lembrarEm,
      recorrencia,
      origem: searchParams.get("text") ? REMINDER_ORIGIN.COMPARTILHAMENTO : REMINDER_ORIGIN.APP,
    });

    if (!item) return;

    setCapturados((atual) => [item, ...atual]);
    setValor("");
    setChipAtivo(null);
    setPersonalizado(null);
    setErro("");
    inputRef.current?.focus();

    flush().catch(() => {});

    // O convite para notificar aparece no primeiro lembrete COM hora - e o
    // unico momento em que ele faz sentido para quem esta usando.
    if (lembrarEm && pushSuportado() && permissaoAtual() === "default") {
      setConvitePush(true);
    }
  }, [lembrarEm, recorrencia, searchParams, textoFinal, valor]);

  function aoTeclar(evento) {
    if (evento.key === "Enter") {
      evento.preventDefault();
      capturar();
    }
  }

  async function habilitarNotificacoes() {
    setAtivandoPush(true);
    setErro("");

    try {
      await ativarNotificacoes();
      setConvitePush(false);
    } catch (error) {
      setErro(error?.message || "Nao foi possivel ativar as notificacoes.");
    } finally {
      setAtivandoPush(false);
    }
  }

  const precisaInstalar = precisaInstalarParaNotificar();

  return (
    <div className="capturar-page">
      <header className="capturar-header">
        <button
          type="button"
          className="capturar-voltar"
          onClick={() => navigate("/dashboard")}
          aria-label="Voltar"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>

        <span className="capturar-titulo">Capturar</span>

        <button
          type="button"
          className="capturar-inbox"
          onClick={() => navigate("/lembretes")}
        >
          <FontAwesomeIcon icon={faInbox} /> Lembretes
        </button>
      </header>

      <div className="capturar-campo">
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <input
          ref={inputRef}
          className="capturar-input"
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCorrect="on"
          autoFocus
          placeholder="O que voce nao pode esquecer?"
          value={valor}
          onChange={(evento) => setValor(evento.target.value)}
          onKeyDown={aoTeclar}
        />

        <button
          type="button"
          className="capturar-botao"
          onClick={capturar}
          disabled={!textoFinal && !valor.trim()}
          aria-label="Adicionar lembrete"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

      <div className="capturar-chips">
        {CHIPS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className={`capturar-chip ${
              chipAtivo === chip.id && !personalizado ? "ativo" : ""
            }`}
            onClick={() => {
              setPersonalizado(null);
              setChipAtivo((atual) => (atual === chip.id ? null : chip.id));
            }}
            disabled={Boolean(analise.lembrarEm)}
          >
            {chip.rotulo}
          </button>
        ))}

        <button
          type="button"
          className={`capturar-chip personalizar ${personalizado ? "ativo" : ""}`}
          onClick={() => setMostrarPersonalizar(true)}
          disabled={Boolean(analise.lembrarEm)}
        >
          <FontAwesomeIcon icon={faSliders} /> Personalizar
        </button>
      </div>

      <div className={`capturar-preview ${lembrarEm ? "com-alarme" : ""}`}>
        <FontAwesomeIcon icon={lembrarEm ? faBell : faBellSlash} />
        {lembrarEm ? (
          <span>
            Vou te avisar <strong>{formatarLembrarEm(lembrarEm)}</strong>
            {ehRecorrente(recorrencia) && (
              <>
                {" "}
                · <strong>{getRecurrenceLabel(recorrencia)}</strong>
              </>
            )}
          </span>
        ) : (
          <span>Sem alarme — vai direto para a caixa de entrada</span>
        )}
      </div>

      <p className="capturar-dica">
        Escreva o horario junto: <b>amanha 15h</b>, <b>em 30min</b>, <b>daqui 2h</b>
      </p>

      {erro && (
        <div className="capturar-erro">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {erro}
        </div>
      )}

      {convitePush && !precisaInstalar && (
        <div className="capturar-convite">
          <div>
            <strong>Quer que eu toque no horario?</strong>
            <span>Sem isso o lembrete so aparece quando voce abrir o app.</span>
          </div>
          <div className="capturar-convite-acoes">
            <button type="button" onClick={() => setConvitePush(false)}>
              Agora nao
            </button>
            <button type="button" className="primario" onClick={habilitarNotificacoes} disabled={ativandoPush}>
              {ativandoPush ? "Ativando..." : "Ativar"}
            </button>
          </div>
        </div>
      )}

      {convitePush && precisaInstalar && (
        <div className="capturar-convite">
          <div>
            <strong>No iPhone, instale o app primeiro</strong>
            <span>
              Safari → Compartilhar → Adicionar a Tela de Inicio. O iOS so libera
              notificacoes para o app instalado.
            </span>
          </div>
          <div className="capturar-convite-acoes">
            <button type="button" onClick={() => setConvitePush(false)}>
              Entendi
            </button>
          </div>
        </div>
      )}

      <section className="capturar-lista">
        {capturados.length === 0 ? (
          <div className="capturar-vazio">
            <FontAwesomeIcon icon={faCheck} />
            <span>Escreva, aperte Enter, escreva de novo.</span>
            <small>O campo nao fecha — da para despejar tudo de uma vez.</small>
          </div>
        ) : (
          capturados.map((item) => {
            const naFila = uuidsPendentes.includes(item.client_uuid);

            return (
              <article key={item.client_uuid} className="capturar-item">
                <span className={`capturar-item-status ${naFila ? "pendente" : "salvo"}`}>
                  <FontAwesomeIcon icon={naFila ? faCloudArrowUp : faCheck} />
                </span>

                <div className="capturar-item-conteudo">
                  <strong>{item.texto}</strong>
                  <small>
                    {item.lembrar_em ? (
                      <>
                        <FontAwesomeIcon icon={faClock} /> {formatarLembrarEm(item.lembrar_em)}
                      </>
                    ) : (
                      "Sem alarme"
                    )}
                    {ehRecorrente(item.recorrencia) &&
                      ` · ${getRecurrenceLabel(item.recorrencia)}`}
                    {naFila && " · aguardando rede"}
                  </small>
                </div>
              </article>
            );
          })
        )}
      </section>

      <PersonalizarLembreteModal
        show={mostrarPersonalizar}
        valorInicial={personalizado}
        onHide={() => setMostrarPersonalizar(false)}
        onExited={() => inputRef.current?.focus()}
        onConfirm={(escolha) => {
          setPersonalizado(escolha.lembrarEm ? escolha : null);
          setChipAtivo(null);
          setMostrarPersonalizar(false);
        }}
      />
    </div>
  );
};

export default CapturarPage;
